import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';

const ContactInfo = ({ icon, title, content }: { icon: React.ReactNode; title: string; content: React.ReactNode }) => (
  <motion.div 
    className="flex items-start space-x-4 group"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <div className="text-[#c9ab81] mt-1 group-hover:scale-110 transition-transform duration-300">
      {icon}
    </div>
    <div>
      <h3 className="text-lg font-serif text-white mb-2">{title}</h3>
      <div className="text-white/80">{content}</div>
    </div>
  </motion.div>
);

const Contact = () => {
  const [activeTab, setActiveTab] = useState<'general' | 'retreat'>('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await emailjs.sendForm(
        'YOUR_SERVICE_ID',
        'YOUR_TEMPLATE_ID',
        formRef.current,
        'YOUR_PUBLIC_KEY'
      );
      setSubmitStatus('success');
      formRef.current.reset();
    } catch (error)
    {
      setSubmitStatus('error');
      console.error('Error sending email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://res.cloudinary.com/dansvy9a5/image/upload/v1739113189/1736183551342_2_ylx0mt.jpg')"
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
          </div>
        </motion.div>
        
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            className="text-center max-w-4xl mx-auto px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <h1 className="text-5xl md:text-7xl font-serif text-white mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto font-light">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
          {/* Contact Info */}
          <div className="space-y-12">
            <ContactInfo 
              icon={<MapPin className="w-6 h-6" />}
              title={t('footer.contact')}
              content={<p>Maurice</p>}
            />
            <ContactInfo 
              icon={<Phone className="w-6 h-6" />}
              title="Téléphone"
              content={<p>+23058241702</p>}
            />
            <ContactInfo 
              icon={<Mail className="w-6 h-6" />}
              title="Email"
              content={<p>auraimmersionilemaurice@gmail.com</p>}
            />
            <ContactInfo 
              icon={<Clock className="w-6 h-6" />}
              title={t('footer.hours')}
              content={
                <>
                  <p>{t('footer.schedule.weekdays')}</p>
                  <p>{t('footer.schedule.saturday')}</p>
                  <p>{t('footer.schedule.sunday')}</p>
                </>
              }
            />
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-black/50 rounded-2xl p-8 shadow-soft backdrop-blur-sm">
              {/* Form Type Selector */}
              <div className="flex space-x-4 mb-8">
                <button
                  className={`flex-1 py-3 px-6 rounded-lg font-serif transition-all duration-300 ${
                    activeTab === 'general'
                      ? 'bg-[#c9ab81] text-white'
                      : 'bg-[#9AA581]/5 text-white hover:bg-[#9AA581]/10'
                  }`}
                  onClick={() => setActiveTab('general')}
                >
                  {t('contact.tabs.general')}
                </button>
                <button
                  className={`flex-1 py-3 px-6 rounded-lg font-serif transition-all duration-300 ${
                    activeTab === 'retreat'
                      ? 'bg-[#c9ab81] text-white'
                      : 'bg-[#9AA581]/5 text-white hover:bg-[#9AA581]/10'
                  }`}
                  onClick={() => setActiveTab('retreat')}
                >
                  {t('contact.tabs.retreat')}
                </button>
              </div>

              {/* Form */}
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-serif text-white mb-2">
                      {t('contact.form.lastName')}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full bg-[#9AA581]/5 border border-[#41392e]/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#c9ab81] transition-colors duration-300"
                      placeholder={t('contact.form.lastName')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-serif text-white mb-2">
                      {t('contact.form.firstName')}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full bg-[#9AA581]/5 border border-[#41392e]/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#c9ab81] transition-colors duration-300"
                      placeholder={t('contact.form.firstName')}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-serif text-white mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full bg-[#9AA581]/5 border border-[#41392e]/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#c9ab81] transition-colors duration-300"
                      placeholder={t('contact.form.email')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-serif text-white mb-2">
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full bg-[#9AA581]/5 border border-[#41392e]/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#c9ab81] transition-colors duration-300"
                      placeholder={t('contact.form.phone')}
                    />
                  </div>
                </div>
                {activeTab === 'retreat' && (
                  <div>
                    <label className="block text-sm font-serif text-white mb-2">
                      {t('contact.form.dates')}
                    </label>
                    <input
                      type="text"
                      name="dates"
                      className="w-full bg-[#9AA581]/5 border border-[#41392e]/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#c9ab81] transition-colors duration-300"
                      placeholder={t('contact.form.dates')}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-serif text-white mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    name="message"
                    required
                    className="w-full bg-[#9AA581]/5 border border-[#41392e]/10 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[#c9ab81] transition-colors duration-300 min-h-[150px] resize-none"
                    placeholder={
                      activeTab === 'general'
                        ? t('contact.form.messageGeneral')
                        : t('contact.form.messageRetreat')
                    }
                  />
                </div>

                <input type="hidden" name="formType" value={activeTab} />
                <input type="hidden" name="to_email" value="auraimmersionilemaurice@gmail.com" />

                <motion.button
                  type="submit"
                  className="w-full bg-[#c9ab81] text-white py-4 rounded-lg font-serif text-lg hover:bg-[#41392e] transition-colors duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isSubmitting}
                >
                  <span>
                    {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                  </span>
                  <Send className="w-5 h-5" />
                </motion.button>

                {submitStatus === 'success' && (
                  <p className="text-green-500 text-center">{t('contact.form.success')}</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-500 text-center">{t('contact.form.error')}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="h-[500px] relative mt-20">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d239573.8249849941!2d57.34396395!3d-20.2175508!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x217c504df94474c9%3A0x4203d9c2116bd031!2sMaurice!5e0!3m2!1sfr!2sfr!4v1709655144949!5m2!1sfr!2sfr"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="grayscale contrast-125 opacity-90"
        />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-[#f5f5f5] via-transparent to-[#f5f5f5] opacity-50" />
        </div>
      </div>
    </div>
  );
};

export default Contact;